import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link } from "../router";
import PropertyCard from "../components/PropertyCard";
import { properties, ASSET_CLASSES } from "../data/properties";

export default function PropertiesPage() {
  const [params, setParams] = useSearchParams();
  const initialClass = params.get("class") || "All";
  const initialQ = params.get("q") || "";

  const [filter, setFilter] = useState(initialClass);
  const [q, setQ] = useState(initialQ);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    setFilter(params.get("class") || "All");
    setQ(params.get("q") || "");
  }, [params]);

  const setClass = (c) => {
    setFilter(c);
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("class"); else next.set("class", c);
    setParams(next, { replace: true });
  };

  const results = useMemo(() => {
    let list = properties.filter((p) => filter === "All" || p.assetClass === filter);
    if (q.trim()) {
      const t = q.toLowerCase();
      list = list.filter((p) =>
        [p.title, p.locality, p.city, p.assetClass, p.blurb].join(" ").toLowerCase().includes(t)
      );
    }
    if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "newest") list = [...list].sort((a, b) => a.verifiedDays - b.verifiedDays);
    return list;
  }, [filter, q, sort]);

  return (
    <>
      <section className="dark section-sm" style={{ paddingBottom: "clamp(40px,5vw,72px)" }}>
        <div className="shell">
          <span className="eyebrow on-dark reveal">Portfolio · Live inventory only</span>
          <h1 className="h-xl reveal" style={{ marginTop: 16 }}>The curated portfolio.</h1>
          <p className="lead reveal" style={{ marginTop: 16, maxWidth: 560 }}>
            Every asset below is title-vetted by our in-house legal team and
            re-verified on a fixed cadence. If it's here, it's real and available.
          </p>

          {/* Smart search */}
          <div className="reveal" style={{ marginTop: 32, display: "flex", gap: 10, alignItems: "center", background: "rgba(249,249,251,0.06)", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 4, padding: 6, maxWidth: 640 }}>
            <span style={{ paddingLeft: 12, color: "var(--champagne)" }}>⌕</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search or describe your requirement…"
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "var(--paper-ink)", fontSize: "1rem", padding: "10px 4px" }}
            />
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section style={{ position: "sticky", top: 84, zIndex: 40, background: "rgba(255,255,255,0.82)", backdropFilter: "saturate(180%) blur(14px)", borderBottom: "1px solid var(--hairline)" }}>
        <div className="shell" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "16px 0", flexWrap: "wrap" }}>
          <div className="flexrow" style={{ gap: 8 }}>
            {["All", ...ASSET_CLASSES].map((c) => (
              <button
                key={c}
                onClick={() => setClass(c)}
                className={`chip ${filter === c ? "active" : ""}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flexrow" style={{ gap: 14 }}>
            <span className="muted" style={{ fontSize: "0.82rem" }}>{results.length} live</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ padding: "8px 14px", border: "1px solid var(--hairline)", borderRadius: 2, fontSize: "0.8rem", letterSpacing: "0.04em", background: "transparent", color: "var(--ink-soft)" }}>
              <option value="featured">Featured</option>
              <option value="newest">Recently verified</option>
              <option value="low">Price · low to high</option>
              <option value="high">Price · high to low</option>
            </select>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="section" style={{ paddingTop: "clamp(40px,5vw,64px)" }}>
        <div className="shell">
          {results.length > 0 ? (
            <div className="grid props-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
              {results.map((p) => <PropertyCard key={p.id} p={p} />)}
            </div>
          ) : (
            <div className="center" style={{ padding: "80px 0" }}>
              <h3 className="h-md">Nothing matches — yet.</h3>
              <p className="muted" style={{ margin: "12px auto 24px", maxWidth: 440 }}>
                We'd rather show you nothing than show you dead inventory. Post your
                exact requirement and our intent engine will alert you the moment a
                verified match lists.
              </p>
              <Link to="/buy" className="btn btn-ink">Post a requirement <span className="arr">→</span></Link>
            </div>
          )}
        </div>
      </section>

      <style>{`
        @media (max-width: 980px) { .props-grid { grid-template-columns: repeat(2,1fr) !important; } }
        @media (max-width: 620px) { .props-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </>
  );
}
